import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setRowsPerPage, setStartDate } from "../../../../redux/cartItemRequestSlice";
import { formatTimestamp } from "../../../../utility/Utility";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
// import { useLazyGetCartItemRequestsQuery } from "../../../../redux/services/cartToCartOrder";

import "./index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import ButtonComponent from "../../../../components/ButtonComponent";
import { useCartToCartOrders } from "../../../../hooks/useFirebaseOrders";

export interface CartItemRequest {
  id: string;
  cart?: {
    id: string;
    displayName: string;
  };
  restaurantId: string;
  orderItems: Array<{
    displayName: string;
    menuItemId: string;
    quantity: number;
    unitPrice: number;
    itemType: string;
    portionSize: string | null;
  }>;
  status?: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

// Move getCartName above CartItemRequestList so it is initialized before use
const getCartName = (request: CartItemRequest) => {
  return request.cart?.displayName || "No Cart Assigned";
};

const CartItemRequestList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cartItemRequests, setCartItemRequests] = useState<CartItemRequest[]>([]);
  // read rowsPerPage and startDate from redux so they persist across navigation
  const rowsPerPage = useSelector((state: any) => state.cartItemRequest?.rowsPerPage ?? 10);
  const reduxStartDate = useSelector((state: any) => state.cartItemRequest?.startDate ?? new Date().toISOString().slice(0,10));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // local startDate mirrors reduxStartDate; updates dispatch to redux
  const [startDate, setStartDateLocal] = useState<string>(reduxStartDate);

  // status filter: 'all' | 'pending' | 'completed' | 'cancelled'
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // keep local startDate in sync if redux value changes (e.g., persisted state on reload)
  useEffect(() => {
    setStartDateLocal(reduxStartDate);
  }, [reduxStartDate]);

  const handleView = (request: CartItemRequest) => {
    navigate(`/orders/cart-item-request/${request.id}`, {
      state: {
        cartItemRequest: request,
        cartName: getCartName(request)
      }
    });
  };

  const selectedRestaurantId = useSelector((state: any) => state.restaurant.selectedRestaurantDetails?.id);
  const { data: cartItemRequestsData, loading: isCartItemRequestsLoading, error } = useCartToCartOrders(selectedRestaurantId,reduxStartDate);  

  useEffect(() => {
    if (cartItemRequestsData) {
      if (Array.isArray(cartItemRequestsData.orders)) {
        setCartItemRequests(cartItemRequestsData.orders);
      } else {
        setCartItemRequests([]);
      }
      setCurrentPage(1); // reset page on data change
    }
  }, [cartItemRequestsData]);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
    setCurrentPage(1);
  };

  // Filter requests by cart name and status (case-insensitive)
  const filteredRequests = cartItemRequests.filter((request) => {
    const matchesName = getCartName(request).toLowerCase().includes(searchTerm.trim().toLowerCase());
    const status = (request.status || "").toLowerCase();
    const matchesStatus = statusFilter === "all" ? true : status === statusFilter;
    return matchesName && matchesStatus;
  });

  const totalRows = filteredRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Card className="mt-3">
        <CardHeader className="bg-white">
          <h4>Cart Item Transfer Dashboard</h4>
        </CardHeader>
        <CardBody>
          {isCartItemRequestsLoading ? (
            <TableLoadingComponent numColumns={5} />
          ) : error ? (
            <div className="alert alert-danger">
              Error loading cart item requests
            </div>
          ) : (
            <div>
              {/* Search and Filter Bar */}
              <div className="d-flex align-items-center justify-space-between">
                <div className="cart-item-request-list-searchbar d-flex align-items-center mb-3">
                  <input
                    type="text"
                    className="form-control me-2 cart-item-request-list-search"
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <select
                    className="form-select me-2 cart-item-request-list-filter"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <input
                    type="date"
                    className="form-control me-2"
                    value={startDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStartDateLocal(v);
                      dispatch(setStartDate(v));
                    }}
                    title="Filter by request date"
                  />
                </div>
              </div>
              {/* Requests List */}
              {filteredRequests.length === 0 ? (
                <div className="text-muted">No requests found.</div>
              ) : (
                <>
                  {paginatedRequests.map((request) => (
                    <div key={request.id} className="cart-item-request-list-card mb-3 p-3 border rounded bg-white shadow-sm">
                      <div className="d-flex justify-content-between align-items-middle">
                        <div>
                          <strong>Request From {getCartName(request)}</strong>
                          <div className="text-muted cart-item-request-list-fromto">
                            From: <strong>{getCartName(request)}</strong> To: <strong>Kitchen</strong>
                          </div>
                          <div className="text-muted cart-item-request-list-total mt-2">
                            Total Items Requested: {request.orderItems.reduce((sum, item) => sum + item.quantity, 0)}
                          </div>
                          <div className="text-muted cart-item-request-list-date">
                            {formatTimestamp(request.createdAt)}
                          </div>
                          {/* Items breakdown by category */}
                          <div className="mt-2">
                            {(() => {
                              // Group items by category/type
                              const itemsByCategory: { [key: string]: any[] } = {};
                              request.orderItems.forEach(item => {
                                // Extract category from display name (e.g., "Rice & Curry" from "Chicken Rice & Curry")
                                const category = item.displayName.includes('Rice & Curry') ? 'Rice & Curry' : 
                                               item.displayName.includes('Sides') ? 'Sides' : 
                                               item.displayName.includes('Noodles') ? 'Noodles' :
                                               item.displayName.includes('Kottu') ? 'Kottu' :
                                               'Other';
                                
                                if (!itemsByCategory[category]) {
                                  itemsByCategory[category] = [];
                                }
                                itemsByCategory[category].push(item);
                              });

                              return Object.entries(itemsByCategory).map(([category, items]) => (
                                <div key={category} className="mb-2">
                                  {/* <div className="fw-bold text-dark">{category}</div> */}
                                  {items.map((item, idx) => (
                                    <div key={idx} className="d-flex justify-content-between align-items-center ms-3 cart-item-request-item-row">
                                      <div className="d-flex align-items-center">
                                        <div className={`cart-item-request-dot me-2 ${
                                          item.itemType === 'Gold' ? 'cart-item-request-dot-gold' : 
                                          item.itemType === 'Silver' ? 'cart-item-request-dot-silver' : 
                                          'cart-item-request-dot-default'
                                        }`}></div>
                                        {item.displayName && (
                                          <span className={`badge ms-2 ${
                                            item.itemType === 'Gold' ? 'bg-warning text-dark' : 
                                            item.itemType === 'Silver' ? 'bg-secondary' : 
                                            'bg-light text-dark'
                                          }`}>
                                            {item.displayName}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-end">
                                        <div className="text-muted small">Requested: <strong>{item.quantity}</strong></div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                        <div>
                          <span className={`badge ${request.status?.toLowerCase() === 'pending' ? 'bg-warning text-dark' : request.status?.toLowerCase() === 'completed' ? 'bg-success text-white' : request.status?.toLowerCase() === 'cancelled' ? 'bg-secondary text-white' : 'bg-secondary text-dark'} me-2`}>{request.status}</span>
                          <ButtonComponent titel="View Details" onClick={() => handleView(request)}/>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Pagination footer */}
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <div className="text-muted pagination-info">
                        Showing {totalRows === 0 ? 0 : ((currentPage - 1) * rowsPerPage + 1)} to {Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows}
                      </div>
                    <div className="d-flex align-items-center pagination-controls">
                      <select
                        className="form-select rows-select"
                        value={rowsPerPage}
                        onChange={(e) => handleChangeRowsPerPage(Number(e.target.value))}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      {/* First page */}
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        title="First Page"
                      >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        <FontAwesomeIcon icon={faAngleLeft} />
                      </button>
                      {/* Previous page */}
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        title="Previous Page"
                      >
                        <FontAwesomeIcon icon={faAngleLeft} />
                      </button>
                      <span style={{ minWidth: 60, textAlign: "center" }}>
                        Page {currentPage} / {totalPages}
                      </span>
                      {/* Next page */}
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        title="Next Page"
                      >
                        <FontAwesomeIcon icon={faAngleRight} />
                      </button>
                      {/* Last page */}
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        title="Last Page"
                      >
                        <FontAwesomeIcon icon={faAngleRight} />
                        <FontAwesomeIcon icon={faAngleRight} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default CartItemRequestList;